// Reusable, dependency-free SVG sparkline + KPI card.
// Designed for compact executive dashboards. Reused across:
//   - Phase 2: dedupe rate / supplier wins / merge confidence
//   - Phase 3: failover success trend (when wired)
//   - Phase 4: payment gateway success trend
//
// Props
// -----
// label           : string · big-letter chart title
// value           : string · current value displayed on top-right (formatted)
// delta           : number · trend pct vs. previous half-window (optional)
// data            : Array<{x: string|Date, y: number, tooltip?: string}>
//                   exactly one number per bucket; nulls/undefined → treated as 0
// color           : tailwind color stem ("red" | "green" | "blue" | "amber" | "gray")
// height          : number · SVG height (default 56)
// areaFill        : boolean (default true)
//
import React, { useMemo, useState } from 'react';

const COLOR_MAP = {
  red:   { stroke: '#dc2626', fill: 'rgba(220, 38, 38, 0.15)',  chip: 'text-red-700 bg-red-50 border-red-200' },
  green: { stroke: '#16a34a', fill: 'rgba(22, 163, 74, 0.15)',  chip: 'text-green-700 bg-green-50 border-green-200' },
  blue:  { stroke: '#2563eb', fill: 'rgba(37, 99, 235, 0.15)',  chip: 'text-blue-700 bg-blue-50 border-blue-200' },
  amber: { stroke: '#d97706', fill: 'rgba(217, 119, 6, 0.15)',  chip: 'text-amber-700 bg-amber-50 border-amber-200' },
  gray:  { stroke: '#6b7280', fill: 'rgba(107, 114, 128, 0.15)', chip: 'text-gray-700 bg-gray-50 border-gray-200' },
};

export const Sparkline = ({ data = [], color = 'red', height = 56, areaFill = true, ariaLabel = 'trend' }) => {
  const c = COLOR_MAP[color] || COLOR_MAP.red;
  const [hover, setHover] = useState(null);

  const { points, areaPath, linePath, max, min } = useMemo(() => {
    if (!data || data.length === 0) {
      return { points: [], areaPath: '', linePath: '', max: 0, min: 0 };
    }
    const ys = data.map((d) => Number.isFinite(d?.y) ? d.y : 0);
    const max = Math.max(...ys, 0.0001);
    const min = Math.min(...ys, 0);
    const span = max - min || 1;
    const w = 200;
    const h = height;
    const pad = 4;
    const innerH = h - pad * 2;
    const stepX = data.length > 1 ? (w - pad * 2) / (data.length - 1) : 0;
    const pts = ys.map((y, i) => ({
      x: pad + i * stepX,
      y: pad + innerH - ((y - min) / span) * innerH,
    }));
    const linePath = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');
    const areaPath =
      pts.length > 0
        ? `${linePath} L${pts[pts.length - 1].x.toFixed(1)} ${h - pad} L${pts[0].x.toFixed(1)} ${h - pad} Z`
        : '';
    return { points: pts, areaPath, linePath, max, min };
  }, [data, height]);

  if (!data || data.length === 0) {
    return (
      <div className="h-14 flex items-center justify-center text-[11px] text-gray-400" aria-label={ariaLabel}>
        no data yet
      </div>
    );
  }

  const onMove = (idx) => setHover(idx);
  const onLeave = () => setHover(null);

  return (
    <div className="relative" data-testid={`sparkline-${color}`}>
      <svg viewBox={`0 0 200 ${height}`} width="100%" height={height} preserveAspectRatio="none" aria-label={ariaLabel} role="img">
        {areaFill && <path d={areaPath} fill={c.fill} stroke="none" />}
        <path d={linePath} fill="none" stroke={c.stroke} strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" />
        {points.map((p, i) => (
          <g key={i} onMouseEnter={() => onMove(i)} onMouseLeave={onLeave} onTouchStart={() => onMove(i)}>
            <rect x={p.x - 6} y="0" width="12" height={height} fill="transparent" />
            <circle
              cx={p.x}
              cy={p.y}
              r={hover === i ? 3 : 2}
              fill={c.stroke}
              opacity={hover === i ? 1 : 0.7}
            />
          </g>
        ))}
      </svg>

      {hover != null && data[hover] && (
        <div
          className="absolute -top-2 -translate-y-full pointer-events-none rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg px-2 py-1.5 text-[11px] text-gray-700 dark:text-gray-200 z-20"
          style={{ left: `${(hover / Math.max(1, data.length - 1)) * 100}%`, transform: 'translate(-50%, -100%)' }}
          data-testid="sparkline-tooltip"
        >
          {data[hover].tooltip}
        </div>
      )}
    </div>
  );
};

export const SparklineCard = ({ label, value, sub, delta, data, color = 'red', testId, footnote }) => {
  const c = COLOR_MAP[color] || COLOR_MAP.red;
  const deltaPositive = (delta ?? 0) >= 0;
  return (
    <div
      data-testid={testId}
      className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 hover:shadow-md transition-shadow"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 truncate">{label}</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white mt-0.5 tabular-nums">{value}</p>
          {sub && <p className="text-[11px] text-gray-500 mt-0.5">{sub}</p>}
        </div>
        {Number.isFinite(delta) && (
          <span className={`shrink-0 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${c.chip}`}>
            {deltaPositive ? '▲' : '▼'} {Math.abs(delta).toFixed(1)}%
          </span>
        )}
      </div>
      <div className="mt-2">
        <Sparkline data={data} color={color} ariaLabel={label} />
      </div>
      {footnote && <p className="text-[10px] text-gray-400 mt-1">{footnote}</p>}
    </div>
  );
};

export default SparklineCard;
